import * as React from 'react';
import { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  CameraAlt as CameraIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { colors } from '../../utils/colors';
import {
  getAdminProfileAPI,
  updateAdminProfileAPI,
  changeAdminPasswordAPI,
  uploadAdminImageAPI,
} from '../../lib/api';

const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_SERVER_URL || '';
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const CustomInput = memo(({ 
  label, 
  value, 
  onChange, 
  type = 'text', 
  required = false, 
  startIcon, 
  endIcon, 
  helperText,
  fullWidth = true 
}) => {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <Box sx={{ mb: 2, width: fullWidth ? '100%' : 'auto' }}>
      <Typography 
        variant="body2" 
        sx={{ 
          mb: 1, 
          color: colors.text.primary, 
          fontWeight: 500 
        }}
      >
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {startIcon && (
          <Box sx={{ 
            position: 'absolute', 
            left: 12, 
            zIndex: 1,
            display: 'flex',
            alignItems: 'center'
          }}>
            {startIcon}
          </Box>
        )}
        <input
          type={type}
          value={value || ''}
          onChange={handleChange}
          style={{
            width: '100%',
            padding: '12px 16px',
            paddingLeft: startIcon ? '48px' : '16px',
            paddingRight: endIcon ? '48px' : '16px',
            border: `1px solid ${colors.border.main}`,
            borderRadius: '8px',
            fontSize: '16px',
            fontFamily: 'inherit',
            outline: 'none',
            backgroundColor: 'white',
            transition: 'border-color 0.2s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = colors.primary.main;
            e.target.style.boxShadow = `0 0 0 2px ${colors.primary.main}20`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = colors.border.main;
            e.target.style.boxShadow = 'none';
          }}
        />
        {endIcon && (
          <Box sx={{ 
            position: 'absolute', 
            right: 12, 
            zIndex: 1,
            display: 'flex',
            alignItems: 'center'
          }}>
            {endIcon}
          </Box>
        )}
      </Box>
      {helperText && (
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 0.5, 
            color: colors.text.secondary, 
            fontSize: '12px' 
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

const ProfileDrawer = ({ open, onClose, onProfileUpdated, initialProfileData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const hasProfileLoaded = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    avatar: null,
    avatarPreview: null
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (open) {
      if (initialProfileData && !hasProfileLoaded.current) {
        const rawImage = initialProfileData.profile_image || initialProfileData.avatar || null;
        const imageUrl = normalizeImageUrl(rawImage);
        setProfileData({
          name: initialProfileData.name || '',
          email: initialProfileData.email || '',
          avatar: imageUrl,
          avatarPreview: imageUrl
        });
        hasProfileLoaded.current = true;
        setProfileLoading(false);
      } else if (!hasProfileLoaded.current) {
        fetchProfile();
      }
    }
  }, [open, initialProfileData]);

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const response = await getAdminProfileAPI();
      const rawImage = response.data?.profile_image || response.data?.avatar || null;
      const imageUrl = normalizeImageUrl(rawImage);
      setProfileData({
        name: response.data?.name || '',
        email: response.data?.email || '',
        avatar: imageUrl,
        avatarPreview: imageUrl
      });
      hasProfileLoaded.current = true;
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const showAlert = (message, severity = 'info') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setAlert({ show: false, message: '', severity: 'info' });
  };

  const handleProfileChange = useCallback((field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handlePasswordChange = useCallback((field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleNameChange = useCallback((value) => {
    handleProfileChange('name', value);
  }, [handleProfileChange]);

  const handleEmailChange = useCallback((value) => {
    handleProfileChange('email', value);
  }, [handleProfileChange]);

  const handleCurrentPasswordChange = useCallback((value) => {
    handlePasswordChange('currentPassword', value);
  }, [handlePasswordChange]);

  const handleNewPasswordChange = useCallback((value) => {
    handlePasswordChange('newPassword', value);
  }, [handlePasswordChange]);

  const handleConfirmPasswordChange = useCallback((value) => {
    handlePasswordChange('confirmPassword', value);
  }, [handlePasswordChange]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showAlert('Please select a valid image file', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showAlert('Image size should be less than 5MB', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await uploadAdminImageAPI(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const rawImage = response.data?.profile_image || response.data?.imageUrl || response.data?.avatar || null;
        const imageUrl = normalizeImageUrl(rawImage);
        setProfileData(prev => ({
          ...prev,
          avatar: imageUrl || prev.avatar,
          avatarPreview: e.target.result
        }));
        if (onProfileUpdated) {
          onProfileUpdated({ avatar: imageUrl, profile_image: imageUrl, preview: e.target.result });
        }
      };
      reader.readAsDataURL(file);
      
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.name.trim()) {
      showAlert('Name is required', 'error');
      return;
    }

    if (!profileData.email.trim()) {
      showAlert('Email is required', 'error');
      return;
    }

    try {
      setLoading(true);
      await updateAdminProfileAPI({
        name: profileData.name,
        email: profileData.email,
      });
      if (onProfileUpdated) {
        onProfileUpdated({ name: profileData.name, email: profileData.email, avatar: profileData.avatar, profile_image: profileData.avatar });
      }
      showAlert('Profile updated successfully', 'success');
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword) {
      showAlert('Current password is required', 'error');
      return;
    }

    if (!passwordData.newPassword) {
      showAlert('New password is required', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showAlert('New password must be at least 6 characters long', 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showAlert('New password and confirm password do not match', 'error');
      return;
    }

    try {
      setLoading(true);
      await changeAdminPasswordAPI({
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword
      });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      showAlert('Password changed successfully', 'success');
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const ProfileTab = useMemo(() => (
    <Box component="form" onSubmit={handleProfileSubmit} sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Avatar
                src={profileData.avatarPreview}
                sx={{
                  width: 120, 
                  height: 120,
                  bgcolor: colors.primary.main,
                  fontSize: '3rem'
                }}
              >
                {!profileData.avatarPreview && profileData.name.charAt(0).toUpperCase()}
              </Avatar>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                id="avatar-upload"
              />
              <label htmlFor="avatar-upload">
                <IconButton
                  component="span"
                  sx={{
                    position: 'absolute',
                    bottom: -5,
                    right: -5,
                    bgcolor: colors.primary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: colors.primary.dark
                    }
                  }}
                  disabled={loading}
                >
                  <CameraIcon />
                </IconButton>
              </label>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.text.primary }}>
              {profileData.name || 'Admin User'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {profileData.email}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: colors.text.primary }}>
            Profile Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomInput
                label="Full Name"
                value={profileData.name}
                onChange={handleNameChange}
                required
                startIcon={<PersonIcon sx={{ color: colors.primary.main }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                label="Email Address"
                type="email"
                value={profileData.email}
                onChange={handleEmailChange}
                required
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: colors.primary.main,
                minWidth: 120,
                '&:hover': {
                  bgcolor: colors.primary.dark
                }
              }}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  ), [profileData, loading, handleNameChange, handleEmailChange, handleProfileSubmit, handleImageUpload]);

  const PasswordTab = useMemo(() => (
    <Box component="form" onSubmit={handlePasswordSubmit} sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: colors.text.primary }}>
            Change Password
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomInput
                label="Current Password"
                type={showPassword.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={handleCurrentPasswordChange}
                required
                startIcon={<LockIcon sx={{ color: colors.primary.main }} />}
                endIcon={
                  <IconButton
                    onClick={() => togglePasswordVisibility('current')}
                    edge="end"
                    sx={{ p: 1 }}
                  >
                    {showPassword.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                label="New Password"
                type={showPassword.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={handleNewPasswordChange}
                required
                helperText="Password must be at least 6 characters long"
                endIcon={
                  <IconButton
                    onClick={() => togglePasswordVisibility('new')}
                    edge="end"
                    sx={{ p: 1 }}
                  >
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                label="Confirm New Password"
                type={showPassword.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                endIcon={
                  <IconButton
                    onClick={() => togglePasswordVisibility('confirm')}
                    edge="end"
                    sx={{ p: 1 }}
                  >
                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: colors.primary.main,
                minWidth: 120,
                '&:hover': {
                  bgcolor: colors.primary.dark
                }
              }}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  ), [passwordData, showPassword, loading, handleCurrentPasswordChange, handleNewPasswordChange, handleConfirmPasswordChange, handlePasswordSubmit, togglePasswordVisibility]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : 500,
          maxWidth: '100vw',
          bgcolor: colors.background.default
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            bgcolor: colors.primary.main,
            color: 'white'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Profile Settings
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {alert.show && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ show: false, message: '', severity: 'info' })}
            sx={{ m: 2 }}
          >
            {alert.message}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                color: colors.text.secondary,
                '&.Mui-selected': {
                  color: colors.primary.main
                }
              }
            }}
          >
            <Tab
              label="Profile"
              icon={<PersonIcon />}
              iconPosition="start"
            />
            <Tab
              label="Password"
              icon={<LockIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {profileLoading ? (
            <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
          ) : (
            <>
              {activeTab === 0 && ProfileTab}
              {activeTab === 1 && PasswordTab}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProfileDrawer;